import { icons } from '../assets/icons';
import { PROJECT_CONFIG } from '../config/project-info';
import { GiscusWidget } from './GiscusWidget';

export class CommunityViewComponent {
  private giscus: GiscusWidget;

  constructor() {
    this.giscus = new GiscusWidget({
      containerId: 'community-giscus-container',
      mapping: 'specific',
      term: 'Mastui Community Hub',
      category: 'General',
      inputPosition: 'top'
    });
  }

  public render(): string {
    const categories = [
      {
        title: 'Q&A & Setup Help',
        description: 'Ask questions regarding terminal compatibility, OAuth instance tokens, Sixel/TGP graphics setup, or keymaps.',
        icon: icons.messageSquare('w-5 h-5 text-matrix-400'),
        url: `${PROJECT_CONFIG.githubDiscussionsUrl}/categories/q-a`,
        badge: 'Q&A'
      },
      {
        title: 'Ideas & Feature Requests',
        description: 'Propose enhancements, vote on roadmap suggestions, and discuss new timeline or composer features.',
        icon: icons.lightbulb('w-5 h-5 text-amber-400'),
        url: `${PROJECT_CONFIG.githubDiscussionsUrl}/categories/ideas`,
        badge: 'Ideas'
      },
      {
        title: 'Show and Tell & Themes',
        description: 'Share your custom Textual CSS themes, terminal setups, keybinding configs, and scripts with the community.',
        icon: icons.packageIcon('w-5 h-5 text-cyber-400'),
        url: `${PROJECT_CONFIG.githubDiscussionsUrl}/categories/show-and-tell`,
        badge: 'Showcase'
      },
      {
        title: 'General Discussions',
        description: 'Open conversations about Mastodon API protocols, Fediverse developments, and TUI engineering.',
        icon: icons.zap('w-5 h-5 text-matrix-400'),
        url: `${PROJECT_CONFIG.githubDiscussionsUrl}/categories/general`,
        badge: 'General'
      },
      {
        title: 'Announcements',
        description: 'Official release updates, roadmap milestones, and development notices from the maintainer.',
        icon: icons.badgeCheck('w-5 h-5 text-cyber-400'),
        url: `${PROJECT_CONFIG.githubDiscussionsUrl}/categories/announcements`,
        badge: 'Updates'
      }
    ];

    return `
      <div id="community-view-container" class="min-h-screen pt-4 pb-20 font-mono">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <!-- Header Hero -->
          <div class="text-center max-w-3xl mx-auto space-y-4 pt-4">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-matrix-950/80 text-matrix-400 border border-matrix-800 text-xs font-semibold shadow-glow-sm">
              ${icons.github('w-3.5 h-3.5')}
              <span>Powered by GitHub Discussions</span>
            </div>
            
            <h1 class="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Mastui Community & Discussions
            </h1>
            
            <p class="text-xs sm:text-base text-terminal-muted leading-relaxed max-w-2xl mx-auto">
              Connect with fellow terminal enthusiasts, ask configuration questions, share custom keymaps, or propose new features. All comments sync live with GitHub.
            </p>

            <!-- Action Buttons -->
            <div class="flex flex-wrap items-center justify-center gap-3 pt-2">
              <a 
                href="${PROJECT_CONFIG.githubDiscussionsUrl}/new/choose"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-terminal-bgDark bg-matrix-500 hover:bg-matrix-400 shadow-glow text-xs sm:text-sm transition-all"
              >
                ${icons.messageSquare('w-4 h-4')}
                <span>Start a Discussion on GitHub</span>
                ${icons.externalLink('w-3.5 h-3.5 opacity-80')}
              </a>

              <a 
                href="${PROJECT_CONFIG.mastodonUrl}"
                target="_blank"
                rel="me noopener noreferrer"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-cyber-300 bg-cyber-950/80 hover:bg-cyber-900 border border-cyber-700 text-xs sm:text-sm transition-all"
              >
                ${icons.mastodon('w-4 h-4 text-cyber-400')}
                <span>Follow @Mastui</span>
                ${icons.externalLink('w-3.5 h-3.5 opacity-80')}
              </a>
            </div>
          </div>

          <!-- Category Grid -->
          <div class="space-y-4 text-left">
            <h3 class="text-xs font-bold uppercase tracking-wider text-matrix-500 px-1">
              Discussion Categories
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              ${categories.map(cat => `
                <a 
                  href="${cat.url}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-5 rounded-2xl bg-terminal-card border border-terminal-border hover:border-matrix-500/60 shadow-glow-sm flex flex-col justify-between group transition-all"
                >
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <div class="w-10 h-10 rounded-xl bg-terminal-bgDark border border-terminal-border flex items-center justify-center group-hover:scale-105 transition-transform">
                        ${cat.icon}
                      </div>
                      <span class="text-[11px] font-mono px-2 py-0.5 rounded-full bg-terminal-bgDark text-terminal-muted border border-terminal-border">
                        ${cat.badge}
                      </span>
                    </div>

                    <h4 class="font-bold text-white group-hover:text-matrix-400 transition-colors text-sm flex items-center gap-1.5">
                      <span>${cat.title}</span>
                    </h4>

                    <p class="text-xs text-terminal-fg/80 leading-relaxed">
                      ${cat.description}
                    </p>
                  </div>

                  <div class="pt-4 mt-2 flex items-center text-xs font-semibold text-matrix-400 group-hover:text-matrix-300 gap-1">
                    <span>View Category</span>
                    ${icons.externalLink('w-3.5 h-3.5')}
                  </div>
                </a>
              `).join('')}
            </div>
          </div>

          <!-- Live Embedded Discussions Board -->
          <div class="space-y-4 text-left">
            <div class="flex items-center justify-between px-1">
              <div>
                <h3 class="text-base sm:text-lg font-bold text-white">
                  Live Discussion Feed
                </h3>
                <p class="text-xs text-terminal-muted">
                  Post questions or reply to community topics directly from your browser.
                </p>
              </div>

              <a 
                href="${PROJECT_CONFIG.githubDiscussionsUrl}"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs font-semibold text-matrix-400 hover:underline flex items-center gap-1"
              >
                <span>Browse All on GitHub</span>
                ${icons.externalLink('w-3.5 h-3.5')}
              </a>
            </div>

            <div class="p-6 sm:p-8 rounded-2xl bg-terminal-card border border-terminal-border shadow-glow-lg">
              ${this.giscus.render()}
            </div>
          </div>

        </div>
      </div>
    `;
  }

  public mount() {
    this.giscus.mount();
  }

  public destroy() {
    this.giscus.destroy();
  }
}
